import { API_TYPES, get } from "@src/appV2/api";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { environmentConfig } from "@src/appV2/environment";
import { FacilityRoles } from "@src/appV2/FacilityUser/types";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { getUserType } from "@src/appV2/PlacementCandidates/utils/helper";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import {
  type placementApplicationDataSchema,
  type PlacementApplicationIncludedInterview,
  type PlacementApplicationIncludedPlacement,
  type PlacementApplicationIncludedPlacementCandidate,
  type PlacementApplicationIncludedWorker,
  type PlacementApplicationIncludedWorkerRequirement,
  type PlacementApplicationIncludedWorkplace,
  type PlacementApplicationIncludedWorkplaceUser,
  type PlacementApplicationsResponse,
  placementApplicationsResponseSchema,
} from "../types/fetchPlacementApplications.schema";

function fetchPlacementApplicationsApiUrl(workplaceId: string) {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/placement-applications`;
}

export const getPlacementApplicationsForWorkplaceQuerySchema = z.object({
  sort: z.string().optional(),
  filter: z
    .object({
      status: z.string().optional(),
      "placement.id": z.string().optional(),
      "worker.id": z.string().optional(),
      "worker.name": z.string().optional(),
      "workplace.id": z.string().optional(),
      workerTypes: z.string().optional(),
      jobTypes: z.string().optional(),
      shiftTypes: z.string().optional(),
      assignedTo: z.string().optional(),
    })
    .optional(),
  page: z
    .object({
      size: z.number(),
      cursor: z.string().optional(),
    })
    .optional(),
});
export type GetPlacementApplicationsForWorkplaceQuery = z.infer<
  typeof getPlacementApplicationsForWorkplaceQuerySchema
>;

interface FetchPlacementApplicationsParams {
  workplaceId: string;
  queryParams: GetPlacementApplicationsForWorkplaceQuery;
}

export function getFetchPaginatedPlacementApplicationsQueryKey({
  workplaceId,
  queryParams,
}: FetchPlacementApplicationsParams) {
  return [fetchPlacementApplicationsApiUrl(workplaceId), queryParams];
}

export function canFetchPlacementApplications(roles: string[]) {
  const canManageWorkplaceRoles = new Set<string>([
    FacilityRoles.ADMIN,
    FacilityRoles.RECRUITMENT,
    FacilityRoles.SHIFT_MANAGEMENT,
  ]);
  return roles.some((role) => canManageWorkplaceRoles.has(role));
}

export function useFetchPaginatedPlacementApplications(
  params: FetchPlacementApplicationsParams,
  options: UseInfiniteQueryOptions<PlacementApplicationsResponse, AxiosError> = {}
) {
  const { workplaceId, queryParams } = params;
  const { profile, admin, user } = useSession();
  return useInfiniteQuery({
    queryKey: getFetchPaginatedPlacementApplicationsQueryKey({ workplaceId, queryParams }),
    queryFn: async ({
      pageParam: pageParameter = undefined,
    }: QueryFunctionContext<QueryKey, string | undefined>) => {
      const response = await get({
        url: fetchPlacementApplicationsApiUrl(workplaceId),
        queryParams: {
          ...queryParams,
          page: {
            size: queryParams.page?.size ?? 20,
            cursor: pageParameter,
          },
        },
        responseSchema: placementApplicationsResponseSchema,
        requestSchema: getPlacementApplicationsForWorkplaceQuerySchema,
      });
      logEvent(APP_V2_USER_EVENTS.PLACEMENT_APPLICATIONS_PAGE_VIEWED, {
        workplaceId,
        userType: getUserType(profile, admin),
        userId: user?._id,
        email: user?.email,
        queryParams,
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage.links.nextCursor ?? undefined,
    ...options,
  });
}

export function transformResponseIntoPlacementApplication(
  placementApplication: z.infer<typeof placementApplicationDataSchema>,
  placementApplicationIncludedMetaData: PlacementApplicationsResponse["included"]
) {
  const placementCandidate = placementApplicationIncludedMetaData.find(
    (included) =>
      included.type === API_TYPES.placementCandidate &&
      included.id === placementApplication.relationships.placementCandidate.data.id
  ) as PlacementApplicationIncludedPlacementCandidate | undefined;

  if (!placementCandidate) {
    throw new Error("Placement candidate details not found");
  }

  const placement = placementApplicationIncludedMetaData.find(
    (included) =>
      included.type === API_TYPES.placement &&
      included.id === placementApplication.relationships.placement.data.id
  ) as PlacementApplicationIncludedPlacement | undefined;

  if (!placement) {
    throw new Error("Placement details not found");
  }

  const workplace = placementApplicationIncludedMetaData.find(
    (included) =>
      included.type === API_TYPES.workplace &&
      included.id === placement.relationships.workplace.data.id
  ) as PlacementApplicationIncludedWorkplace | undefined;

  if (!workplace) {
    throw new Error("Workplace details not found");
  }

  const worker = placementApplicationIncludedMetaData.find(
    (included) =>
      included.type === API_TYPES.worker &&
      included.id === placementCandidate.relationships.worker.data.id
  ) as PlacementApplicationIncludedWorker | undefined;

  if (!worker) {
    throw new Error("Worker details not found");
  }

  const interviews = placementApplicationIncludedMetaData.filter(
    (included): included is PlacementApplicationIncludedInterview =>
      included.type === API_TYPES.interview &&
      included.attributes.placementApplicationId === placementApplication.id
  );

  const workerRequirements = placementApplication.relationships.workerRequirements?.data.map(
    (workerRequirement) => {
      const workerRequirementData = placementApplicationIncludedMetaData.find(
        (included): included is PlacementApplicationIncludedWorkerRequirement =>
          included.type === API_TYPES.workerRequirement && included.id === workerRequirement.id
      );
      if (!workerRequirementData) {
        throw new Error("Worker requirement details not found");
      }

      return workerRequirementData;
    }
  );

  const assignedToRelationship = placementApplication.relationships.assignedTo?.data;
  const assignedTo = assignedToRelationship
    ? (() => {
        const assignedToUser = placementApplicationIncludedMetaData.find(
          (included): included is PlacementApplicationIncludedWorkplaceUser =>
            included.type === API_TYPES.workplaceUser && included.id === assignedToRelationship.id
        );
        return assignedToUser
          ? { userId: assignedToRelationship.id, name: assignedToUser.attributes.name }
          : undefined;
      })()
    : undefined;

  return {
    ...placementApplication.attributes,
    id: placementApplication.id,
    assignedTo,
    placement: {
      ...placement.attributes,
      id: placement.id,
      workplace: { ...workplace.attributes, id: workplace.id },
    },
    placementCandidate: {
      ...placementCandidate.attributes,
      id: placementCandidate.id,
      worker: { ...worker.attributes, id: worker.id },
    },
    interviews: interviews.map((interview) => ({
      ...interview.attributes,
      id: interview.id,
    })),
    workerRequirements: workerRequirements ?? [],
  };
}

export type PlacementApplicationWithDetails = ReturnType<
  typeof transformResponseIntoPlacementApplication
>;
